import { AppAdmin } from 'api/app-admin'

/**
 * Sanitize file name before upload:
 * - strip diacritics (Vietnamese accents → ASCII)
 * - replace whitespace with `-`
 * - drop characters outside [a-zA-Z0-9._-]
 */
const sanitizeFileName = (name: string): string => {
  const trimmed = name.trim()
  if (!trimmed) return 'file'
  return (
    trimmed
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9._-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^[-_.]+|[-_.]+$/g, '') || 'file'
  )
}

const withSafeName = (file: File): File => {
  const safe = sanitizeFileName(file.name)
  if (safe === file.name) return file
  return new File([file], safe, { type: file.type, lastModified: file.lastModified })
}

/**
 * Upload a single file via the shared `/portal-tokenize/upload` endpoint.
 * Returns the uploaded file path/URL on success.
 */
export const uploadFileToBackend = async (file: File): Promise<string> => {
  const formData = new FormData()
  formData.append('file', withSafeName(file))
  const response = await AppAdmin.uploadSingleFile(formData)
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Upload failed')
  }
  return response.data.data.filePath as string
}

/**
 * Upload multiple property-activity images via `/admin/property-activities/upload-images`.
 * Field name: `images` (multipart). Returns array of uploaded URLs.
 */
export const uploadPropertyActivityImages = async (files: File[]): Promise<string[]> => {
  if (files.length === 0) return []
  const formData = new FormData()
  files.forEach((file) => formData.append('images', withSafeName(file)))
  const response = await AppAdmin.uploadPropertyActivityImages(formData)
  const urls = (response.data?.data?.urls ?? response.data?.urls) as string[] | undefined
  if (!urls) {
    throw new Error(response.data?.message || 'Upload images failed')
  }
  return urls
}

/**
 * Upload multiple property-activity documents (PDF) via
 * `/admin/property-activities/upload-documents`. Field name: `documents`.
 */
export const uploadPropertyActivityDocuments = async (files: File[]): Promise<string[]> => {
  if (files.length === 0) return []
  const formData = new FormData()
  files.forEach((file) => formData.append('documents', withSafeName(file)))
  const response = await AppAdmin.uploadPropertyActivityDocuments(formData)
  const urls = (response.data?.data?.urls ?? response.data?.urls) as string[] | undefined
  if (!urls) {
    throw new Error(response.data?.message || 'Upload documents failed')
  }
  return urls
}

/**
 * Upload a single property-activity attachment, routing to the right endpoint
 * based on file type (PDF → upload-documents, others → upload-images).
 */
export const uploadPropertyActivityAttachment = async (file: File): Promise<string> => {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  const urls = isPdf
    ? await uploadPropertyActivityDocuments([file])
    : await uploadPropertyActivityImages([file])
  if (!urls[0]) throw new Error('Upload returned no URL')
  return urls[0]
}
